// Детекторы платформы/режима запуска, флаг «инструкцию по установке закрыли» и
// регистрация service worker'а.
// localStorage обёрнут в try-catch — как в auth/storage.ts (приватный режим
// бросает на доступе к localStorage). Ключ с общим префиксом `podpipe.`.

import { registerSW } from 'virtual:pwa-register'
import type { createBrowserRouter } from 'react-router-dom'

const INSTALL_DISMISSED_KEY = 'podpipe.install_dismissed'

export type MobilePlatform = 'ios' | 'android'

// На каком мобильном платформе открыт сайт. null — десктоп/прочее.
export function getMobilePlatform(): MobilePlatform | null {
  const ua = navigator.userAgent
  if (/android/i.test(ua)) return 'android'
  // iPhone/iPod — по UA. iPadOS 13+ маскируется под macOS (UA содержит Macintosh),
  // отличаем по тач-поинтам: у настоящего Mac их нет.
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios'
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return 'ios'
  return null
}

// Запущено ли как установленный PWA (standalone), а не во вкладке браузера.
export function isStandalone(): boolean {
  const standaloneMedia = window.matchMedia('(display-mode: standalone)').matches
  // iOS Safari не поддерживает display-mode, но выставляет navigator.standalone.
  const iosStandalone = (navigator as { standalone?: boolean }).standalone === true
  return standaloneMedia || iosStandalone
}

export function loadInstallDismissed(): boolean {
  try {
    return localStorage.getItem(INSTALL_DISMISSED_KEY) === 'true'
  } catch {
    return false
  }
}

export function saveInstallDismissed(): void {
  try {
    localStorage.setItem(INSTALL_DISMISSED_KEY, 'true')
  } catch {
    // приватный режим — игнорируем
  }
}

// Тип data-router'а берём из самой фабрики, чтобы не завязываться на имя, под
// которым react-router его экспортирует.
type DataRouter = ReturnType<typeof createBrowserRouter>

// Предикат «обновление сейчас применять нельзя». Регистрирует плеер: пока медиа играет,
// перезагрузка оборвала бы воспроизведение. Отложенное обновление применится на
// следующем триггере (hidden / смена маршрута), когда предикат отпустит.
let updateBlocker: () => boolean = () => false

export function setUpdateBlocker(predicate: (() => boolean) | null): void {
  updateBlocker = predicate ?? (() => false)
}

/**
 * Регистрация service worker и **тихое** применение обновлений — без плашки в UI.
 *
 * SW собран с `registerType: 'prompt'`: новый воркер встаёт в waiting и сам управление не
 * перехватывает. Это защищает открытую вкладку — страницы у нас lazy-чанки (см. router.tsx),
 * и подмена precache под ногами дала бы «Failed to fetch dynamically imported module»
 * вместо страницы.
 *
 * Применяем обновление в двух точках:
 * - уход приложения в фон (`hidden`) — основной путь, перезагрузку никто не видит;
 * - смена маршрута — подстраховка для тех, кто держит приложение открытым часами.
 *
 * Проверяем наличие обновления при возврате из фона: в standalone-PWA вкладка живёт сутками,
 * и штатной перепроверки SW браузером может просто не случиться.
 */
export function setupPwaAutoUpdate(router: DataRouter): void {
  let needRefresh = false
  let registration: ServiceWorkerRegistration | undefined

  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      needRefresh = true
    },
    onRegisteredSW(_swUrl, r) {
      registration = r
    },
  })

  // `updateSW(true)` = skipWaiting + перезагрузка страницы по controllerchange.
  // needRefresh не сбрасываем, пока заблокировано, — повторим на следующем триггере.
  function applyPendingUpdate() {
    if (!needRefresh || updateBlocker()) return
    needRefresh = false
    void updateSW(true)
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      applyPendingUpdate()
      return
    }
    // Офлайн или недоступный сервер — не наша забота, следующий возврат из фона повторит.
    void registration?.update().catch(() => undefined)
  })

  // Подписка на data-router, а не useLocation: хук работал бы только внутри маршрутов, а
  // AppLayout не покрывает /login, /register, /recovery. Здесь видны все переходы.
  let lastPathname = router.state.location.pathname
  router.subscribe((state) => {
    if (state.navigation.state !== 'idle') return
    if (state.location.pathname === lastPathname) return
    lastPathname = state.location.pathname
    applyPendingUpdate()
  })
}
